/**
 * Service categories screen (DNKADM-0052 U1) — the `v2_service_category` list +
 * create/edit/deactivate/reactivate (handoff §5.3). `RequireAdmin`-gated route + a
 * Configuration nav item.
 *
 * Categories are a purely a labeling/grouping taxonomy, **decoupled from credits**
 * (§5.3) — this screen carries NO credit logic. Loads `listServiceCategories({
 * status })` through `useResource`, keyed on the Active/Inactive/All filter
 * (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of name → description →
 * status with Edit + a three-way `status`-gated action (DRAFT→Publish,
 * ACTIVE→Deactivate, INACTIVE→Reactivate). The status chip shows the lifecycle
 * `status` (Draft/Active/Inactive, DNKAPI-0261) now, not the raw `isActive`. "Add
 * category" and Edit open the {@link ServiceCategoryDialog}. Every write refetches +
 * toasts. A first-run empty tenant (§8) shows a strong empty state with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name/description search and the Active/Inactive/All filter (`useUrlActiveFilter`);
 * every data column has a shared {@link SortableHeader} (default Name ascending). Search,
 * filter and sort live in the URL (`serviceCategoriesListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateServiceCategory,
  listServiceCategories,
  publishServiceCategory,
  reactivateServiceCategory,
  type ServiceCategory,
} from '@/api/serviceCategories';
import { ServiceCategoryDialog } from './ServiceCategoryDialog';
import {
  SERVICE_CATEGORIES_URL_DEFAULTS,
  SERVICE_CATEGORY_DEFAULT_SORT,
  SERVICE_CATEGORY_SORT_KEYS,
  categoryName,
  categoryStatusLabel,
  serviceCategoryMatches,
  serviceCategorySortValue,
  type ServiceCategorySortKey,
} from './serviceCategoriesListView';
import { ScreenError } from '@/components/ScreenError';

export function ServiceCategoriesScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL
  // (`serviceCategoriesListView.ts`).
  const url = useListUrlState(SERVICE_CATEGORIES_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(
    url.values.sort,
    url.values.dir,
    SERVICE_CATEGORY_SORT_KEYS,
    SERVICE_CATEGORY_DEFAULT_SORT,
  );

  const categories = useResource(
    useCallback(() => listServiceCategories({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Dialog state: `null` = closed; `{ category: null }` = add; `{ category }` = edit.
  const [dialog, setDialog] = useState<{ category: ServiceCategory | null } | null>(null);

  const refetch = categories.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (categories.data ?? []).filter((category) => serviceCategoryMatches(category, search)),
        (category) => serviceCategorySortValue(category, sort.key),
        sort.dir,
        (category) => category.id,
      ),
    [categories.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: ServiceCategorySortKey) =>
      setUrl(toSortParams(toggleSort(sort, key), SERVICE_CATEGORY_DEFAULT_SORT)),
    [setUrl, sort],
  );

  const handleDeactivate = useCallback(
    async (category: ServiceCategory) => {
      try {
        await deactivateServiceCategory(category.id);
        refetch();
        showToast('Category deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the category.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (category: ServiceCategory) => {
      try {
        await reactivateServiceCategory(category.id);
        refetch();
        showToast('Category reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the category.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handlePublish = useCallback(
    async (category: ServiceCategory) => {
      try {
        await publishServiceCategory(category.id);
        refetch();
        showToast('Category published', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t publish the category.', 'error');
      }
    },
    [refetch, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Service categories
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Labels you group services by. Categories are tags only — they don’t affect credits.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ category: null })}
          sx={{ flexShrink: 0 }}
        >
          Add category
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search categories',
          disabled: categories.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter categories by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {categories.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading service categories"
          sx={{ borderRadius: 3 }}
        />
      )}

      {categories.status === 'error' && (
        <ScreenError
          error={categories.error}
          what="the service categories"
          onRetry={categories.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {categories.status === 'ready' && categories.data && categories.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="sell" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active'
              ? 'No categories yet'
              : 'No categories match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first category to start grouping and labeling services.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ category: null })}
              >
                Add category
              </Button>
            </>
          )}
        </Paper>
      )}

      {categories.status === 'ready' && categories.data && categories.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Service categories" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Description" col="description" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{categoryName(category)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {category.description ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={categoryStatusLabel(category)}
                      color={
                        category.status === 'DRAFT'
                          ? 'warning'
                          : category.status === 'INACTIVE'
                            ? 'default'
                            : 'success'
                      }
                      variant={category.status === 'ACTIVE' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconAction
                      size="small"
                      label={`Edit category ${category.name || category.id}`}
                      onClick={() => setDialog({ category })}
                    >
                      <Icon name="edit" />
                    </IconAction>
                    {category.status === 'DRAFT' && (
                      <IconAction
                        size="small"
                        label={`Publish category ${category.name || category.id}`}
                        onClick={() => void handlePublish(category)}
                      >
                        <Icon name="publish" />
                      </IconAction>
                    )}
                    {category.status === 'ACTIVE' && (
                      <IconAction
                        size="small"
                        label={`Deactivate category ${category.name || category.id}`}
                        onClick={() => void handleDeactivate(category)}
                      >
                        <Icon name="toggle_off" />
                      </IconAction>
                    )}
                    {category.status === 'INACTIVE' && (
                      <IconAction
                        size="small"
                        label={`Reactivate category ${category.name || category.id}`}
                        onClick={() => void handleReactivate(category)}
                      >
                        <Icon name="toggle_on" />
                      </IconAction>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {categories.status === 'ready' &&
        categories.data &&
        categories.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="categories" query={search} />}

      <ServiceCategoryDialog
        open={dialog !== null}
        category={dialog?.category ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
